import { StatefulHindcastProvider } from "@sofarocean/weather-cubes";
import { Remote, wrap, releaseProxy } from "comlink";
import { FieldLabel } from "./calculate-field-extrema-labels";
import {
  AsyncWeatherProvider,
  AsyncWeatherProviderConstructor,
} from "./weather.worker";

export default class AsyncWeatherProviderProxy {
  private worker: Worker;
  private proxy: Remote<AsyncWeatherProvider> | null;

  constructor(
    readonly onMetadataUpdate: (meta: string) => void,
    readonly weatherVariables: { modelID: string; variableID: string }[],
    readonly requestedStartTime: Date,
    readonly requestedEndTime: Date,
    readonly simulatedHistoricalTimestring?: string
  ) {
    this.worker = new Worker(new URL("./weather.worker.ts", import.meta.url));
    this.proxy = null;
  }
  public async init(): Promise<void> {
    if (this.proxy == null) {
      const factory = wrap<AsyncWeatherProviderConstructor>(this.worker);
      this.proxy = await new factory(
        this.onMetadataUpdate,
        this.weatherVariables,
        this.requestedStartTime,
        this.requestedEndTime,
        this.simulatedHistoricalTimestring
      );
      // Forecast provider needs to be initialized before components can use it
      await this.proxy.init();
    }
  }
  public async dispose() {
    if (this.proxy) this.proxy[releaseProxy]();
    this.worker.terminate();
  }

  // proxy functions //
  public getHindcastValue: StatefulHindcastProvider["getHindcastValue"] =
    async (params) => await this.proxy?.getHindcastValue(params);
  public getHindcastTimeSeries: StatefulHindcastProvider["getHindcastTimeSeries"] =
    async (params) => await this.proxy?.getHindcastTimeSeries(params);
  public getRequestTimes: () => Promise<
    | Readonly<{
        simulatedHistoricalTime: Date | undefined;
        requestedStartTime: Date;
        requestedEndTime: Date;
      }>
    | undefined
  > = async () => await this.proxy?.getRequestTimes();
  public setWeatherVariables: StatefulHindcastProvider["setWeatherVariables"] =
    async (params) => await this.proxy?.setWeatherVariables(params);
  public buildWebGLBuffer: AsyncWeatherProvider["buildWebGLBuffer"] = async (
    ...params
  ) => (await this.proxy?.buildWebGLBuffer(...params)) ?? "NO_DATA";
  public generateTiles: AsyncWeatherProvider["generateTiles"] = async (
    ...params
  ) => (await this.proxy?.generateTiles(...params)) ?? [];
  public setRegionOfInterest: StatefulHindcastProvider["setRegionOfInterest"] =
    async (region) => await this.proxy?.setRegionOfInterest(region);
  public setSimulatedHistoricalTime = async (timestring?: string) =>
    await this.proxy?.setSimulatedHistoricalTime(timestring);

  public calculateWeatherVariableExtremaLabels = async (
    stepSizeDeg: number,
    modelID: string,
    variableID: string
  ): Promise<FieldLabel[] | undefined> =>
    await this.proxy?.calculateWeatherVariableExtremaLabels(
      stepSizeDeg,
      modelID,
      variableID
    );
}
